import React from "react";
import { PropTypes } from "prop-types";
import { useTranslation } from "react-i18next";
import {
    MrbFieldInput,
    MrbEditPage,
    MrbFieldTextArea,
    MrbFieldToggle,
    MrbFieldSelect,
    MrbFieldRadioButtons,
    MrbFieldNumericInput,
    MrbButton,
} from "mrb/components";
import { NakiScheduleFieldDatePicker } from "../../common/components";
import { components } from "react-select";
import { MrbContentLoader } from "mrb/components/loader";
import {
    AddPodcastsToTheGroupLoader,
    AddPodcastVolumeItem,
    PodcastCoverImageUpload,
} from "administration/podcast/components";
import { MrbFileUploadValidation } from "mrb/components/media";
import {
    AddStationsOrPodcastsLoader,
    CoverImageUploadLoader,
    FieldRichTextEditor,
    ProducerFieldSelect,
} from "common/components";
import { defaultTemplate } from "common/hoc";
import { PodcastEditPodcastVolumeListTemplate } from "../components";
import { mergeCss } from "mrb/common/utils";

function PodcastEditTemplate({ podcastEditViewStore, t }) {
    const {
        form,
        loaderStore,
        episodeSortTypeOptions,
        coverImageUploadViewStore,
        genreSelectStore,
        languageSelectStore,
        locationSelectStore,
        overrideRadioUsersSettingsIsVisible,
        overrideAppUsersSettingsIsVisible,
        overrideHandheldPinkUsersSettingsIsVisible,
        overrideHandheldUsersSettingsIsVisible,
        producerSelectStore,
        podcastContentTypeSelectStore,
        isTorahAnytimeTypeSelected,
        podcastCategorySelectStore,
        torahAnytimeTopicSelectStore,
        torahAnytimeSpeakerSelectStore,
        isTorahAnytimePlaylistTypeSelected,
        isSelectSpeakersDisabled,
        selectAllTorahAnytimeMaleSpeakers,
        selectAllTorahAnytimeFemaleSpeakers,
        showScheduledRelease,
    } = podcastEditViewStore;
    const { fileUploadStore } = coverImageUploadViewStore;

    return (
        <MrbEditPage
            store={podcastEditViewStore}
            contentClassNameExtend={mergeCss(form.$("isFemale").value && "is-female-content")}
            t={t}
        >
            <div className="row">
                <div className="col col-sml-12 col-xxlrg-7 col-huge-8 u-mar--top--sml u-mar--bottom--sml">
                    <div className="c-card--primary">
                        <MrbContentLoader
                            loaderStore={loaderStore}
                            loaderRender={() => <AddStationsOrPodcastsLoader podcast />}
                        >
                            {() => (
                                <div className="u-animation--fadeIn">
                                    <div className="row">
                                        <div className="col col-sml-12 col-lrg-7 col-xxlrg-12 col-huge-7">
                                            <MrbFieldSelect
                                                t={t}
                                                store={podcastContentTypeSelectStore}
                                                classNameExtend="c-dropdown--dark"
                                            />
                                        </div>
                                    </div>
                                    {isTorahAnytimePlaylistTypeSelected && (
                                        <div className="row">
                                            <div className="col col-sml-12 col-lrg-7 col-xxlrg-12 col-huge-7">
                                                <MrbFieldSelect
                                                    t={t}
                                                    store={torahAnytimeTopicSelectStore}
                                                    classNameExtend="c-dropdown--dark"
                                                    hideSelectedOptions={false}
                                                    closeMenuOnSelect={false}
                                                    components={{
                                                        ValueContainer: TopicsDropdownValueContainer,
                                                    }}
                                                />

                                                <MrbFieldSelect
                                                    t={t}
                                                    isDisabled={isSelectSpeakersDisabled}
                                                    store={torahAnytimeSpeakerSelectStore}
                                                    classNameExtend="c-dropdown--dark"
                                                    hideSelectedOptions={false}
                                                    closeMenuOnSelect={false}
                                                    components={{
                                                        ValueContainer: SpeakersDropdownValueContainer,
                                                    }}
                                                    infoLabelRenderer={() => (
                                                        <SpeakersDropdownControls
                                                            onClickAllMaleSpeakers={selectAllTorahAnytimeMaleSpeakers}
                                                            onClickAllFemaleSpeakers={
                                                                selectAllTorahAnytimeFemaleSpeakers
                                                            }
                                                            isDisabled={isSelectSpeakersDisabled}
                                                            t={t}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className="u-separator--primary"></div>

                                    <div className="row">
                                        <div className="col col-sml-12 col-lrg-7 col-xxlrg-12 col-huge-7">
                                            <MrbFieldInput
                                                t={t}
                                                field={form.$("title")}
                                                classNameExtend="c-input--outline"
                                            />
                                            <MrbFieldInput
                                                t={t}
                                                disabled={
                                                    isTorahAnytimeTypeSelected || isTorahAnytimePlaylistTypeSelected
                                                }
                                                field={form.$("podcastFeed.feedUrl")}
                                                classNameExtend="c-input--outline"
                                            />
                                            <MrbFieldInput
                                                t={t}
                                                field={form.$("descriptionLink")}
                                                classNameExtend="c-input--outline"
                                            />
                                            <MrbFieldSelect
                                                t={t}
                                                store={locationSelectStore}
                                                classNameExtend="c-dropdown--dark"
                                            />
                                            <MrbFieldSelect
                                                t={t}
                                                store={languageSelectStore}
                                                classNameExtend="c-dropdown--dark"
                                            />
                                            <MrbFieldSelect
                                                t={t}
                                                store={genreSelectStore}
                                                classNameExtend="c-dropdown--dark"
                                            />
                                            <ProducerFieldSelect
                                                t={t}
                                                store={producerSelectStore}
                                                classNameExtend="c-dropdown--dark"
                                            />

                                            <MrbFieldInput
                                                t={t}
                                                field={form.$("keywords")}
                                                classNameExtend="c-input--outline"
                                            />
                                            <MrbFieldTextArea
                                                t={t}
                                                field={form.$("description")}
                                                classNameExtend="c-input--outline"
                                            />
                                            <FieldRichTextEditor field={form.$("alternativeDescription")} t={t} />
                                            {form.$("isPremium").value ? (
                                                <FieldRichTextEditor field={form.$("premiumInfo")} t={t} />
                                            ) : null}

                                            <div className="u-display--flex">
                                                <div className="u-mar--right--lrg">
                                                    <MrbFieldNumericInput
                                                        precision={0}
                                                        t={t}
                                                        disabled={isTorahAnytimeTypeSelected}
                                                        field={form.$("podcastFeed.episodeLimit")}
                                                    />
                                                </div>
                                                <div className="u-mar--right--lrg">
                                                    <MrbFieldRadioButtons
                                                        field={form.$("podcastFeed.episodeSortTypeId")}
                                                        t={t}
                                                        disabled={
                                                            isTorahAnytimeTypeSelected ||
                                                            isTorahAnytimePlaylistTypeSelected
                                                        }
                                                        options={episodeSortTypeOptions}
                                                    />
                                                </div>
                                                <MrbFieldNumericInput
                                                    classNameOverride={"c-input c-input--sml c-input--outline"}
                                                    precision={2}
                                                    t={t}
                                                    disabled={!form.$("isPremium").value}
                                                    field={form.$("price")}
                                                />
                                            </div>
                                        </div>

                                        <div className="col col-sml-12 col-lrg-5 col-xxlrg-12 col-huge-5">
                                            <div className="c-toggle__card-list">
                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    field={form.$("isActive")}
                                                />
                                                {showScheduledRelease && (
                                                    <div className="c-schedule-release">
                                                        <div className="c-schedule-release__label">{t(form.$("scheduledReleaseAt").label)}</div>
                                                        <NakiScheduleFieldDatePicker
                                                            field={form.$("scheduledReleaseAt")}
                                                            showLabel={false}
                                                            t={t}
                                                            showTimeSelect={true}
                                                            timeIntervals={60}
                                                            onChangeRaw={(e) => e.preventDefault()}
                                                            dateFormat="MM/dd/yyyy h aa"
                                                            minDate={new Date()}
                                                            isClearable={true}
                                                        />
                                                    </div>
                                                )}
                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    disabled={
                                                        isTorahAnytimeTypeSelected || isTorahAnytimePlaylistTypeSelected
                                                    }
                                                    field={form.$("isPremium")}
                                                />
                                                <MrbFieldToggle
                                                    t={t}
                                                    classNameExtend="c-toggle__item--inline"
                                                    field={form.$("isTurnedOnByDefault")}
                                                />
                                                <div className="u-separator--primary u-mar--none"></div>
                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    field={form.$("isVisible")}
                                                />
                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    field={form.$("isFeatured")}
                                                />
                                                {overrideRadioUsersSettingsIsVisible ? (
                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("overrideRadioUsersSettings")}
                                                    />
                                                ) : null}
                                                <div className="u-separator--primary u-mar--none"></div>
                                                {/* Every per-device block sits together from here down, the App
                                                    included - it used to sit above the Portal settings, which
                                                    read as if it belonged to them. */}
                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    field={form.$("isVisibleInApp")}
                                                    disabled={form.$("isFemale").value}
                                                />
                                                <MrbFieldToggle
                                                    t={t}
                                                    classNameExtend="c-toggle__item--inline"
                                                    field={form.$("isTurnedOnByDefaultInApp")}
                                                    disabled={form.$("isFemale").value}
                                                />

                                                <div className="u-separator--primary u-mar--none"></div>

                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    field={form.$("isVisibleInEmbeddedApp")}
                                                    disabled={form.$("isFemale").value}
                                                />
                                                <MrbFieldToggle
                                                    t={t}
                                                    classNameExtend="c-toggle__item--inline"
                                                    field={form.$("isTurnedOnByDefaultInEmbeddedApp")}
                                                    disabled={form.$("isFemale").value}
                                                />

                                                <div className="u-separator--primary u-mar--none"></div>

                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    field={form.$("isVisibleInHandheldApp")}
                                                    disabled={form.$("isFemale").value}
                                                />
                                                <MrbFieldToggle
                                                    t={t}
                                                    classNameExtend="c-toggle__item--inline"
                                                    field={form.$("isTurnedOnByDefaultInHandheldApp")}
                                                    disabled={form.$("isFemale").value}
                                                />

                                                <div className="u-separator--primary u-mar--none"></div>

                                                {/* The Kedayo Pink is the only device that carries female content,
                                                    so unlike the Kedayo block above these stay usable while
                                                    isFemale is set. */}
                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    field={form.$("isVisibleInHandheldPinkApp")}
                                                />
                                                <MrbFieldToggle
                                                    t={t}
                                                    classNameExtend="c-toggle__item--inline"
                                                    field={form.$("isTurnedOnByDefaultInHandheldPinkApp")}
                                                />
                                                {overrideAppUsersSettingsIsVisible ? (
                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("overrideAppUsersSettings")}
                                                    />
                                                ) : null}
                                                {overrideHandheldPinkUsersSettingsIsVisible ? (
                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("overrideHandheldPinkUsersSettings")}
                                                    />
                                                ) : null}
                                                {overrideHandheldUsersSettingsIsVisible ? (
                                                    <MrbFieldToggle
                                                        classNameExtend="c-toggle__item--inline"
                                                        t={t}
                                                        field={form.$("overrideHandheldUsersSettings")}
                                                    />
                                                ) : null}

                                                {/* Female content governs which devices may carry this podcast at
                                                    all, so it sits on its own after the per-device blocks rather
                                                    than inside any one of them. Same placement as the preview page
                                                    and the sub-podcast panels. */}
                                                <div className="u-separator--primary u-mar--none"></div>
                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    field={form.$("isFemale")}
                                                />

                                                <div className="u-separator--primary u-mar--none"></div>
                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    field={form.$("podcastFeed.recentlyUpdatedVisibility")}
                                                />

                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    disabled={
                                                        isTorahAnytimeTypeSelected || isTorahAnytimePlaylistTypeSelected
                                                    }
                                                    field={form.$("podcastFeed.hlsEnabled")}
                                                />

                                                <div className="u-separator--primary u-mar--none"></div>
                                                <MrbFieldToggle
                                                    classNameExtend="c-toggle__item--inline"
                                                    t={t}
                                                    field={form.$("excludedFromRevenue")}
                                                />

                                                <div className="u-separator--primary u-mar--none"></div>

                                                <MrbFieldSelect
                                                    t={t}
                                                    store={podcastCategorySelectStore}
                                                    classNameExtend="c-dropdown--dark"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </MrbContentLoader>
                    </div>
                </div>

                <div className="col col-sml-12 col-xxlrg-4 u-mar--top--sml u-mar--bottom--sml">
                    <div className="c-card--primary">
                        <MrbContentLoader loaderStore={loaderStore} loaderRender={() => <CoverImageUploadLoader />}>
                            {() => (
                                <PodcastCoverImageUpload
                                    classNameExtendUpload="c-upload--tny"
                                    loaderStore={loaderStore}
                                    coverImageUploadViewStore={coverImageUploadViewStore}
                                    isFullWidthFooterButtons
                                    showInfo
                                />
                            )}
                        </MrbContentLoader>
                    </div>
                    <MrbFileUploadValidation store={fileUploadStore} />
                </div>

                <div className="col col-sml-12 col-xxlrg-8 u-mar--top--sml u-mar--top--med">
                    <div>
                        <h1 className="u-type--base u-mar--bottom--sml">
                            {t("ADMINISTRATION.PODCAST.EDIT.ADD_PODCASTVOLUME.TITLE")}
                        </h1>

                        <div className="c-card--primary">
                            <MrbContentLoader
                                loaderStore={loaderStore}
                                loaderRender={() => <AddPodcastsToTheGroupLoader />}
                            >
                                {() => <AddPodcastVolumeItem podcastEditViewStore={podcastEditViewStore} />}
                            </MrbContentLoader>
                        </div>
                    </div>

                    <div className="u-mar--top--sml">
                        <PodcastEditPodcastVolumeListTemplate podcastEditViewStore={podcastEditViewStore} t={t} />
                    </div>
                </div>
            </div>
        </MrbEditPage>
    );
}

PodcastEditTemplate.propTypes = {
    podcastEditViewStore: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

function SpeakersDropdownControls({ onClickAllMaleSpeakers, onClickAllFemaleSpeakers, isDisabled, t }) {
    return (
        <div>
            <MrbButton
                className="c-btn c-btn--xsmall c-btn--anchor u-mar--right--sml"
                label={t("ADMINISTRATION.PODCAST.EDIT.TORAH_ANYTIME.ALL_RABBIS")}
                onClick={onClickAllMaleSpeakers}
                disabled={isDisabled}
            />
            <MrbButton
                className="c-btn c-btn--xsmall c-btn--anchor u-mar--right--sml"
                label={t("ADMINISTRATION.PODCAST.EDIT.TORAH_ANYTIME.ALL_REBBETZINS")}
                onClick={onClickAllFemaleSpeakers}
                disabled={isDisabled}
            />
        </div>
    );
}

function SpeakersDropdownValueContainer(props) {
    const { t } = useTranslation();
    const selected = props.getValue();
    if (selected.length > 10) {
        //if there is children then render children[1] which is search input
        const input = Array.isArray(props.children) ? props.children[1] : null;
        return (
            <components.ValueContainer {...props}>
                <span className="u-type--color--primary">
                    {t("ADMINISTRATION.PODCAST.EDIT.TORAH_ANYTIME.SELECTED_SPEAKERS_COUNT", { count: selected.length })}
                </span>
                {input}
            </components.ValueContainer>
        );
    }

    return <components.ValueContainer {...props} />;
}

function TopicsDropdownValueContainer(props) {
    const { t } = useTranslation();
    const selected = props.getValue();
    if (selected.length > 10) {
        //if there is children then render children[1] which is search input
        const input = Array.isArray(props.children) ? props.children[1] : null;
        return (
            <components.ValueContainer {...props}>
                <span className="u-type--color--primary">
                    {t("ADMINISTRATION.PODCAST.EDIT.TORAH_ANYTIME.SELECTED_TOPICS_COUNT", { count: selected.length })}
                </span>
                {input}
            </components.ValueContainer>
        );
    }

    return <components.ValueContainer {...props} />;
}

export default defaultTemplate(PodcastEditTemplate);
